import * as React from "react";
import {useRef, useState} from "react";
import {LoginContentAdminApi} from "../../../api/LoginContentAdminApi";
import {IToastNotification} from "../../../utils/planUserRemoval";
import {isValidUrl} from "../../../utils/commonUtil";
import {CarouselBadge, CarouselCard} from "../../../model/RegionLoginContent.model";
import {LoginContentApi} from "../../../api/LoginContentApi";

export const ADMIN_LOGIN_CAROUSEL_CREATE_BTN = "Create Card";
export const ADMIN_LOGIN_CAROUSEL_SAVE_BTN = "Save Card";
export const ADMIN_LOGIN_CAROUSEL_HEADLINE_REQUIRED = "Headline is required";
export const ADMIN_LOGIN_CAROUSEL_CTA_LABEL_REQUIRED = "CTA label is required";
export const ADMIN_LOGIN_CAROUSEL_CTA_URL_REQUIRED = "CTA URL is required";
export const ADMIN_LOGIN_CAROUSEL_CTA_URL_INVALID =
    "CTA URL must be a valid URL with a proper domain (e.g. https://www.aon.com)";
export const ADMIN_LOGIN_CAROUSEL_IMAGE_TYPE_ERROR = "Only JPEG or PNG images are allowed";
export const ADMIN_LOGIN_CAROUSEL_IMAGE_UPLOAD_ERROR = "Failed to upload image";
export const ADMIN_LOGIN_CAROUSEL_IMAGE_HINT = "Recommended: 1200x800px, JPG or PNG, under 2MB";

export const BADGE_OPTIONS: {value: CarouselBadge; label: string}[] = [
    {value: "NO_BADGE", label: "No badge"},
    {value: "EVENT", label: "Event (red)"},
    {value: "INSIGHT", label: "Insight (teal)"},
    {value: "REPORT", label: "Report (subtle)"}
];

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png"];

interface CardFormState {
    headline: string;
    badge: CarouselBadge;
    description: string;
    ctaLabel: string;
    ctaUrl: string;
    imageDocumentId: number | null;
    imageFileName: string | null;
}

const toFormState = (card?: CarouselCard): CardFormState => ({
    headline: card?.headline ?? "",
    badge: card?.badge ?? "NO_BADGE",
    description: card?.description ?? "",
    ctaLabel: card?.ctaLabel ?? "",
    ctaUrl: card?.ctaUrl ?? "",
    imageDocumentId: card?.imageDocumentId ?? null,
    imageFileName: card?.imageDocumentId ? "Current image" : null
});

interface AdminLoginCarouselCardFormProps {
    initialCard?: CarouselCard;
    isEdit: boolean;
    setToast: (toast: IToastNotification) => void;
    onSubmit: (card: CarouselCard) => void;
    onCancel: () => void;
}

export const AdminLoginCarouselCardForm: React.FunctionComponent<AdminLoginCarouselCardFormProps> = (props) => {
    const [form, setForm] = useState<CardFormState>(() => toFormState(props.initialCard));
    const [uploading, setUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    // Images uploaded during this editing session. Any that the saved card does not reference
    // (cancelled, replaced, or removed) are deleted on exit; the backend only deletes an image
    // when no card references it, so an existing/published image is never removed.
    const sessionUploads = useRef<number[]>([]);

    const cleanupUnusedUploads = (keepId: number | null) => {
        sessionUploads.current
            .filter(id => id !== keepId)
            .forEach(id => {
                void LoginContentAdminApi.deleteCarouselImage(id).catch(() => undefined);
            });
        sessionUploads.current = [];
    };

    const validate = (): boolean => {
        if (!form.headline.trim()) {
            props.setToast({message: ADMIN_LOGIN_CAROUSEL_HEADLINE_REQUIRED, type: "error"});
            return false;
        }
        if (!form.ctaLabel.trim()) {
            props.setToast({message: ADMIN_LOGIN_CAROUSEL_CTA_LABEL_REQUIRED, type: "error"});
            return false;
        }
        if (!form.ctaUrl.trim()) {
            props.setToast({message: ADMIN_LOGIN_CAROUSEL_CTA_URL_REQUIRED, type: "error"});
            return false;
        }
        if (!isValidUrl(form.ctaUrl.trim())) {
            props.setToast({message: ADMIN_LOGIN_CAROUSEL_CTA_URL_INVALID, type: "error"});
            return false;
        }
        return true;
    };

    const submit = () => {
        if (!validate()) return;
        cleanupUnusedUploads(form.imageDocumentId);
        props.onSubmit({
            headline: form.headline.trim(),
            badge: form.badge,
            description: form.description.trim() ? form.description.trim() : null,
            ctaLabel: form.ctaLabel.trim(),
            ctaUrl: form.ctaUrl.trim(),
            imageDocumentId: form.imageDocumentId,
            active: props.initialCard?.active ?? true
        });
    };

    const cancel = () => {
        cleanupUnusedUploads(null);
        props.onCancel();
    };

    const uploadImage = (file: File) => {
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
            props.setToast({message: ADMIN_LOGIN_CAROUSEL_IMAGE_TYPE_ERROR, type: "error"});
            return;
        }
        const formData = new FormData();
        formData.append("file", file, file.name);
        setUploading(true);
        LoginContentAdminApi.uploadCarouselImage(formData)
            .then((result) => {
                if (result.data != null) sessionUploads.current.push(result.data);
                setForm(prev => ({...prev, imageDocumentId: result.data ?? null, imageFileName: file.name}));
            })
            .catch(() => {
                props.setToast({message: ADMIN_LOGIN_CAROUSEL_IMAGE_UPLOAD_ERROR, type: "error"});
            })
            .finally(() => setUploading(false));
    };

    const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) uploadImage(file);
        e.target.value = "";
    };

    const handleImageDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) uploadImage(file);
    };

    const removeImage = () => setForm(prev => ({...prev, imageDocumentId: null, imageFileName: null}));

    const renderImageField = () => {
        if (form.imageDocumentId) {
            return (
                <div className="admin-login-content__image-preview-row">
                    <div className="admin-login-content__image-preview-thumb">
                        <img src={LoginContentApi.carouselImageUrl(form.imageDocumentId)} alt="Card preview" />
                    </div>
                    <div className="admin-login-content__image-preview-info">
                        <div className="admin-login-content__image-preview-name">{form.imageFileName}</div>
                    </div>
                    <button
                        type="button"
                        className="admin-login-content__btn admin-login-content__btn--secondary admin-login-content__btn--xs"
                        onClick={() => fileInputRef.current?.click()}>
                        Replace
                    </button>
                    <button
                        type="button"
                        className="admin-login-content__btn admin-login-content__btn--danger admin-login-content__btn--xs"
                        data-testid="admin-login-carousel__remove-image"
                        onClick={removeImage}>
                        Remove
                    </button>
                </div>
            );
        }
        return (
            <div
                data-testid="admin-login-carousel__image-upload"
                className={`admin-login-content__image-upload-area${isDragOver ? " admin-login-content__image-upload-area--drag-over" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        fileInputRef.current?.click();
                    }
                }}
                onDragOver={e => {
                    e.preventDefault();
                    setIsDragOver(true);
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleImageDrop}>
                <i className="fa-solid fa-cloud-arrow-up" />
                <span>{uploading
                    ? "Uploading…"
                    : isDragOver
                        ? <strong>Drop image to upload</strong>
                        : <><strong>Click to upload</strong> or drag an image here (JPEG, PNG)</>}</span>
            </div>
        );
    };

    return (
        <div className="admin-login-content__card-edit-form" data-testid="admin-login-carousel__form">
            <div className="admin-login-content__card-edit-header">
                <h4>{props.isEdit ? "Edit Card" : "New Card"}</h4>
                <button
                    type="button"
                    className="admin-login-content__btn admin-login-content__btn--secondary admin-login-content__btn--xs"
                    onClick={cancel}>
                    <i className="fa-solid fa-times" /> Close
                </button>
            </div>

            <div className="admin-login-content__form-row">
                <div className="admin-login-content__form-group">
                    <label className="admin-login-content__label">Headline</label>
                    <input
                        type="text"
                        data-testid="admin-login-carousel__headline"
                        className="admin-login-content__input"
                        value={form.headline}
                        onChange={e => setForm(prev => ({...prev, headline: e.target.value}))}/>
                </div>
                <div className="admin-login-content__form-group">
                    <label className="admin-login-content__label">Badge Type</label>
                    <select
                        data-testid="admin-login-carousel__badge"
                        className="admin-login-content__select"
                        value={form.badge}
                        onChange={e => setForm(prev => ({...prev, badge: e.target.value as CarouselBadge}))}>
                        {BADGE_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="admin-login-content__form-group">
                <label className="admin-login-content__label">Description</label>
                <textarea
                    data-testid="admin-login-carousel__description"
                    className="admin-login-content__textarea"
                    rows={2}
                    value={form.description}
                    onChange={e => setForm(prev => ({...prev, description: e.target.value}))}/>
            </div>

            <div className="admin-login-content__form-row">
                <div className="admin-login-content__form-group">
                    <label className="admin-login-content__label">CTA Label</label>
                    <input
                        type="text"
                        data-testid="admin-login-carousel__cta-label"
                        className="admin-login-content__input"
                        value={form.ctaLabel}
                        onChange={e => setForm(prev => ({...prev, ctaLabel: e.target.value}))}/>
                </div>
                <div className="admin-login-content__form-group">
                    <label className="admin-login-content__label">CTA URL</label>
                    <input
                        type="text"
                        data-testid="admin-login-carousel__cta-url"
                        className="admin-login-content__input"
                        value={form.ctaUrl}
                        onChange={e => setForm(prev => ({...prev, ctaUrl: e.target.value}))}/>
                </div>
            </div>

            <div className="admin-login-content__form-group">
                <label className="admin-login-content__label">Image</label>
                {renderImageField()}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    className="admin-login-content__file-input"
                    data-testid="admin-login-carousel__file-input"
                    onChange={handleFileSelected}/>
                <span className="admin-login-content__form-hint">{ADMIN_LOGIN_CAROUSEL_IMAGE_HINT}</span>
            </div>

            <div className="admin-login-content__card-edit-actions">
                <button
                    type="button"
                    className="admin-login-content__btn admin-login-content__btn--primary admin-login-content__btn--sm"
                    data-testid="admin-login-carousel__submit"
                    onClick={submit}>
                    <i className="fa-solid fa-check" /> {props.isEdit
                        ? ADMIN_LOGIN_CAROUSEL_SAVE_BTN
                        : ADMIN_LOGIN_CAROUSEL_CREATE_BTN}
                </button>
                <button
                    type="button"
                    className="admin-login-content__btn admin-login-content__btn--secondary admin-login-content__btn--sm"
                    onClick={cancel}>
                    Cancel
                </button>
            </div>
        </div>
    );
};
